import { useExportQSetLazyQuery, useExportTaskQSetLazyQuery } from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormRadioSelection,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import {
  ExportQSetFormat,
  QSET_EXPORT_FILE_TEMPLATE_OPTIONS,
  QSetExportFileTemplate,
} from '../../constants';

import { QSetExportFormSchema, type QSetExportFormInput } from './qset-export-form-schema';

interface QSetExportFormModalProps {
  open: boolean;
  onClose: () => void;
  qSetId: string;
  //NOTE: When provided, the export is authorized by task membership instead of permission
  taskId?: string;
}

export default function QSetExportFormModal(props: Readonly<QSetExportFormModalProps>) {
  const { open: isOpen, onClose, qSetId, taskId } = props;

  const { showSnackbar } = useSnackbar();
  const [exportQSet] = useExportQSetLazyQuery();
  const [exportTaskQSet] = useExportTaskQSetLazyQuery();
  const formContext = useForm<QSetExportFormInput>({
    defaultValues: {
      fileType: ExportQSetFormat.Pdf,
      template: QSetExportFileTemplate.QUESTION_WITH_CHOICES,
    },
    resolver: zodResolver(QSetExportFormSchema),
  });

  const { isSubmitting } = formContext.formState;

  const handleSubmit = async (data: QSetExportFormInput) => {
    const hasSolution = data.template === QSetExportFileTemplate.QUESTION_WITH_CHOICES_AND_SOLUTION;

    const input = {
      qSetId,
      format: data.fileType,
      includeSolution: hasSolution,
    };

    const options = {
      fetchPolicy: 'no-cache' as const,
      onCompleted: () => {
        showSnackbar({
          message: 'Export Successfully',
          variant: 'success' as const,
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'Export Failed',
          variant: 'error' as const,
        });
      },
    };

    if (taskId) {
      await exportTaskQSet({ variables: { input: { ...input, taskId } }, ...options });
      return;
    }

    await exportQSet({ variables: { input }, ...options });
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <FormProvider {...formContext}>
        <Form
          onSubmit={(event) => {
            //NOTE: This is a workaround to prevent an over-fetching
            //EXPLANATION: An updateTask api form will trigger when exportQSet, since this modal is inside an updateTask api form
            event.stopPropagation();
            formContext.handleSubmit(handleSubmit)(event);
          }}
        >
          <DialogTitle onClose={onClose}>Export QSet</DialogTitle>
          <Divider />
          <DialogContent>
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              {/* TODO: Uncomment this when exporting to DOCX is ready
              <FormRadioSelection
                name='fileType'
                disabled={isSubmitting}
                options={QSET_EXPORT_FILE_TYPE_OPTIONS}
                label='ประเภทไฟล์'
                row
              /> */}
              <FormRadioSelection
                name='template'
                disabled={isSubmitting}
                options={QSET_EXPORT_FILE_TEMPLATE_OPTIONS}
                label='รายละเอียด'
                row
              />
            </Flex>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button
              color='secondary'
              variant='outlined'
              size='large'
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' size='large' loading={isSubmitting}>
              Export
            </Button>
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
